import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import { useLogo } from '../contexts/LogoContext';
import { 
  Camera, 
  ShieldCheck, 
  CheckCircle2, 
  AlertCircle, 
  Images, 
  ArrowRight,
  Download,
  Eye,
  RefreshCw,
  Sun,
  Smile,
  X,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  SlidersHorizontal,
  FolderDown,
  Layers
} from 'lucide-react';
import api from '../config/api';
import toast from 'react-hot-toast';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

const FindMyPhotosPage = () => {
  const { currentUser } = useAuth();
  const { logoUrl } = useLogo();

  // Albums State
  const [albums, setAlbums] = useState([]);
  const [selectedAlbumId, setSelectedAlbumId] = useState('all');
  const [isLoadingAlbums, setIsLoadingAlbums] = useState(false);

  // Selfie / Search State
  const [selfiePreview, setSelfiePreview] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [similarityThreshold, setSimilarityThreshold] = useState(80);

  // Search Results & Pagination
  const [hasSearched, setHasSearched] = useState(false);
  const [matchedPhotos, setMatchedPhotos] = useState([]);
  const [totalMatches, setTotalMatches] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(24);
  const [selectedPhotosForDownload, setSelectedPhotosForDownload] = useState(new Set());
  const [isDownloadingZip, setIsDownloadingZip] = useState(false);

  // Lightbox Modal State
  const [activeLightboxPhoto, setActiveLightboxPhoto] = useState(null);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const searchBtnRef = useRef(null);
  const resultsRef = useRef(null);

  useEffect(() => {
    fetchAlbums();
    return () => {
      stopCamera();
    };
  }, []);

  const fetchAlbums = async () => {
    try {
      setIsLoadingAlbums(true);
      const res = await api.get('/photos/albums');
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.albums) {
          setAlbums(data.albums);
        }
      }
    } catch (err) {
      console.warn('Could not fetch albums:', err);
    } finally {
      setIsLoadingAlbums(false);
    }
  };

  // Webcam activation
  const startCamera = async () => {
    try {
      setIsCameraActive(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error('Webcam access error:', err);
      toast.error('Unable to access front camera. You can upload a photo instead.');
      setIsCameraActive(false);
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setIsCameraActive(false);
  };

  const captureCameraSelfie = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    setSelfiePreview(dataUrl);
    setSelfieFile(null); // base64 will be used
    stopCamera();
    toast.success('Selfie captured! Click "Find My Photos" below.');
    
    // Smoothly scroll down to bring the pop-up button into focus
    setTimeout(() => {
      searchBtnRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file (JPG, PNG, WEBP).');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setSelfiePreview(event.target.result);
      setSelfieFile(file);
      stopCamera();
      toast.success('Photo uploaded! Click "Find My Photos" below.');
      
      // Smoothly scroll down to bring the pop-up button into focus
      setTimeout(() => {
        searchBtnRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 150);
    };
    reader.readAsDataURL(file);
  };

  const handleSearch = async (pageToFetch = 1) => {
    if (!selfiePreview) {
      toast.error('Please snap a selfie or upload a photo first.');
      return;
    }

    try {
      setIsSearching(true);
      setHasSearched(true); // Open container immediately so user sees live scanning

      // Smoothly scroll down to results section immediately
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);

      const formData = new FormData();

      if (selfieFile) {
        formData.append('selfie', selfieFile);
      } else {
        formData.append('selfieBase64', selfiePreview);
      }

      formData.append('albumId', selectedAlbumId);
      formData.append('threshold', similarityThreshold);
      formData.append('page', pageToFetch);
      formData.append('limit', pageSize);

      const res = await api.upload('/photos/search-by-face', formData);

      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setMatchedPhotos(data.photos || []);
          setTotalMatches(data.totalMatches || 0);
          setCurrentPage(pageToFetch);
          setSelectedPhotosForDownload(new Set());

          if (data.totalMatches === 0) {
            toast('No matches found. Try another selfie with clear lighting.', { icon: '🔍' });
          } else {
            toast.success(`🎉 Found ${data.totalMatches.toLocaleString()} matching photos!`);
          }

          // Smoothly scroll down to the loaded photos
          setTimeout(() => {
            resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 200);
        }
      } else {
        const errData = await res.json().catch(() => ({}));
        toast.error(errData.error || 'Failed to search photos. Please retry.');
      }
    } catch (err) {
      console.error('Search error:', err);
      toast.error('Failed to search photos. Please retry.');
    } finally {
      setIsSearching(false);
    }
  };

  // Toggle selection for batch download
  const toggleSelectPhoto = (photoKey) => {
    setSelectedPhotosForDownload(prev => {
      const next = new Set(prev);
      if (next.has(photoKey)) next.delete(photoKey);
      else next.add(photoKey);
      return next;
    });
  };

  // Download all / selected as ZIP
  const handleDownloadZip = async () => {
    const photosToDownload = selectedPhotosForDownload.size > 0
      ? matchedPhotos.filter(p => selectedPhotosForDownload.has(p.photoKey))
      : matchedPhotos;

    if (photosToDownload.length === 0) {
      toast.error('No photos available to download.');
      return;
    }

    try {
      setIsDownloadingZip(true);
      toast.loading(`Bundling ${photosToDownload.length} high-res photos into ZIP...`, { id: 'zip-download' });

      const zip = new JSZip();
      const folder = zip.folder('BOW_Event_Photos');

      for (let i = 0; i < photosToDownload.length; i++) {
        const photo = photosToDownload[i];
        try {
          const response = await fetch(photo.photoUrl);
          const blob = await response.blob();
          const filename = `BOW_Photo_${i + 1}.jpg`;
          folder.file(filename, blob);
        } catch (fetchErr) {
          console.warn(`Could not fetch photo for zip: ${photo.photoUrl}`, fetchErr);
        }
      }

      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `Beats_Of_Washington_Photos_${new Date().toISOString().split('T')[0]}.zip`);
      toast.success('ZIP downloaded successfully!', { id: 'zip-download' });
    } catch (err) {
      console.error('ZIP generation error:', err);
      toast.error('Failed to create ZIP file.', { id: 'zip-download' });
    } finally {
      setIsDownloadingZip(false);
    }
  };

  const totalPages = Math.ceil(totalMatches / pageSize);

  return (
    <>
      <Helmet>
        <title>Find My Photos - AI Facial Recognition | Beats of Washington</title>
        <meta name="description" content="Find your event photos from Ganesh Festival and Beats of Washington using instant AI face recognition." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-orange-50/50 via-white to-amber-50/30 py-8 sm:py-12">
        <div className="w-full max-w-[1550px] mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Logo Header */}
          <div className="flex justify-center mb-4">
            <Link 
              to="/"
              className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-orange-500 via-amber-600 to-yellow-600 rounded-full flex items-center justify-center overflow-hidden shadow-xl hover:scale-105 transition-transform duration-300 ring-4 ring-orange-200/60"
            >
              {logoUrl ? (
                <img src={logoUrl} alt="Logo" className="w-full h-full object-cover" />
              ) : (
                <span className="text-white font-extrabold text-2xl sm:text-3xl">B</span>
              )}
            </Link>
          </div>

          {/* Hero Section */}
          <div className="text-center max-w-3xl mx-auto mb-8">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-orange-100 text-orange-800 border border-orange-200 text-xs font-bold uppercase tracking-wider mb-2">
              <Camera className="w-3.5 h-3.5 mr-1.5 text-orange-600" />
              AI Face Recognition Photo Finder
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 tracking-tight">
              Find Your Event Moments in{' '}
              <span className="bg-gradient-to-r from-orange-600 via-amber-600 to-yellow-600 bg-clip-text text-transparent">
                Seconds
              </span>
            </h1>
            <p className="mt-3 text-sm sm:text-base text-gray-600">
              Snap a quick selfie or upload a photo to instantly discover all your pictures across 30,000+ Beats of Washington event celebrations.
            </p>
          </div>

          {/* Core Interactive Card */}
          <div className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-200 shadow-xl max-w-4xl mx-auto mb-12">
            
            {/* Step 1: Event Selector Tabs */}
            <div className="mb-6">
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">
                1. Select Event
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedAlbumId('all')}
                  className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all ${
                    selectedAlbumId === 'all'
                      ? 'bg-orange-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  📸 Search All Events
                </button>
                {albums.map((album) => (
                  <button
                    key={album.id}
                    type="button"
                    onClick={() => setSelectedAlbumId(album.id)}
                    className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all ${
                      selectedAlbumId === album.id
                        ? 'bg-orange-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    🪔 {album.title}
                  </button>
                ))}
              </div>
            </div>

            {/* Step 2: Selfie Capture / Upload */}
            <div className="mb-6">
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">
                2. Take a Live Selfie or Upload Photo
              </label>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                
                {/* Camera / Preview Area */}
                <div className="relative aspect-square max-w-xs mx-auto w-full bg-gray-900 rounded-3xl overflow-hidden border-2 border-orange-300 flex items-center justify-center shadow-inner">
                  {isCameraActive ? (
                    <>
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        className="w-full h-full object-cover transform -scale-x-100"
                      />
                      <button
                        type="button"
                        onClick={captureCameraSelfie}
                        className="absolute bottom-4 px-5 py-2.5 rounded-full bg-orange-600 hover:bg-orange-700 text-white font-bold text-xs shadow-xl flex items-center gap-1.5"
                      >
                        <Camera className="w-4 h-4" />
                        Snap Selfie
                      </button>
                    </>
                  ) : selfiePreview ? (
                    <>
                      <img
                        src={selfiePreview}
                        alt="Selfie Preview"
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => setSelfiePreview(null)}
                        className="absolute top-3 right-3 p-1.5 rounded-full bg-black/60 text-white hover:bg-black/80"
                        title="Remove photo"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <div className="text-center p-6 text-gray-400">
                      <Camera className="w-12 h-12 mx-auto mb-2 text-gray-600" />
                      <p className="text-xs">No selfie selected yet</p>
                    </div>
                  )}
                  <canvas ref={canvasRef} className="hidden" />
                </div>

                {/* Controls */}
                <div className="space-y-4">
                  <div className="flex flex-col gap-3">
                    {!isCameraActive ? (
                      <button
                        type="button"
                        onClick={startCamera}
                        className="w-full py-3.5 px-4 rounded-2xl bg-gradient-to-r from-orange-600 to-amber-600 text-white font-bold text-sm shadow-md hover:scale-[1.02] transition-transform flex items-center justify-center gap-2"
                      >
                        <Camera className="w-5 h-5" />
                        <span>Open Front Camera</span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={stopCamera}
                        className="w-full py-3 px-4 rounded-2xl bg-gray-200 text-gray-700 font-bold text-sm hover:bg-gray-300"
                      >
                        Cancel Camera
                      </button>
                    )}

                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      accept="image/*"
                      className="hidden"
                    />

                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full py-3 px-4 rounded-2xl bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold text-sm border border-gray-200 transition-colors flex items-center justify-center gap-2"
                    >
                      <Images className="w-4 h-4 text-orange-600" />
                      <span>Upload Photo from Gallery</span>
                    </button>
                  </div>

                  {/* Selfie Tips */}
                  <div className="bg-orange-50/60 rounded-2xl p-3.5 border border-orange-100 text-xs text-gray-600 space-y-1">
                    <div className="font-bold text-orange-900 flex items-center gap-1">
                      <Sun className="w-3.5 h-3.5 text-orange-600" />
                      Quick Tips for Best Accuracy:
                    </div>
                    <p>• Face camera directly with good, even lighting.</p>
                    <p>• Remove dark sunglasses or face masks.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3: Search Button */}
            <div ref={searchBtnRef} className="pt-4 border-t border-gray-100 scroll-mt-6">
              {selfiePreview && !isSearching && (
                <div className="mb-2 text-center animate-bounce">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-100 text-green-800 text-xs font-bold shadow-xs">
                    <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                    Selfie Ready! Click below to search
                  </span>
                </div>
              )}
              <button
                type="button"
                disabled={!selfiePreview || isSearching}
                onClick={() => handleSearch(1)}
                className={`w-full py-4 rounded-2xl font-extrabold text-base shadow-xl transform transition-all duration-300 flex items-center justify-center gap-2 ${
                  selfiePreview && !isSearching
                    ? 'bg-gradient-to-r from-orange-600 via-amber-600 to-yellow-600 hover:from-orange-700 hover:via-amber-700 hover:to-yellow-700 text-white scale-[1.02] ring-4 ring-orange-300/60 shadow-2xl hover:scale-[1.03] cursor-pointer'
                    : isSearching
                    ? 'bg-orange-600 text-white opacity-90 cursor-wait'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {isSearching ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    <span>AI Scanning 30,000 Photos...</span>
                  </>
                ) : (
                  <>
                    <span>Find My Photos</span>
                    <ArrowRight className={`w-5 h-5 ml-1 ${selfiePreview ? 'animate-pulse' : ''}`} />
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Results Gallery Section */}
          {hasSearched && (
            <div ref={resultsRef} className="space-y-6 animate-slide-up scroll-mt-6">
              
              {/* Searching State Banner */}
              {isSearching ? (
                <div className="bg-white rounded-3xl p-12 text-center border-2 border-orange-200 shadow-xl">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center animate-pulse">
                    <RefreshCw className="w-8 h-8 sm:w-10 sm:h-10 animate-spin text-orange-600" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-black text-gray-900 mb-2">
                    AI Facial Recognition in Progress...
                  </h3>
                  <p className="text-sm text-gray-600 max-w-md mx-auto">
                    Matching your facial vector against high-resolution event albums in AWS Rekognition. Scrolling down to your matches...
                  </p>
                </div>
              ) : (
                <>
                  {/* Results Top Bar */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-white rounded-3xl p-6 border border-gray-200 shadow-sm">
                    <div>
                      <h2 className="text-xl sm:text-2xl font-black text-gray-900 flex items-center gap-2">
                        <Camera className="w-6 h-6 text-orange-600" />
                        Found {totalMatches.toLocaleString()} Matching Photos!
                      </h2>
                      <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                        Showing page {currentPage} of {totalPages || 1}
                      </p>
                    </div>

                    {totalMatches > 0 && (
                      <div className="flex items-center gap-3">
                        <button
                          onClick={handleDownloadZip}
                          disabled={isDownloadingZip}
                          className="px-5 py-2.5 rounded-xl bg-orange-600 hover:bg-orange-700 text-white text-xs sm:text-sm font-bold shadow-md hover:shadow-lg transition-all flex items-center gap-2"
                        >
                          <FolderDown className="w-4 h-4" />
                          <span>
                            {selectedPhotosForDownload.size > 0 
                              ? `Download Selected (${selectedPhotosForDownload.size}) as ZIP`
                              : `Download All (${totalMatches}) as ZIP`}
                          </span>
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Photos Grid */}
                  {matchedPhotos.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 sm:gap-6">
                  {matchedPhotos.map((photo, idx) => {
                    const isSelected = selectedPhotosForDownload.has(photo.photoKey);
                    return (
                      <div
                        key={idx}
                        className={`group relative rounded-2xl overflow-hidden bg-gray-100 aspect-square border transition-all ${
                          isSelected ? 'ring-4 ring-orange-500' : 'border-gray-200 hover:shadow-lg'
                        }`}
                      >
                        <img
                          src={photo.photoUrl}
                          alt={`Match ${idx + 1}`}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />

                        {/* Match Confidence Badge */}
                        <div className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-black/70 backdrop-blur-xs text-white text-[10px] font-bold">
                          ✨ {photo.similarity || 85}% Match
                        </div>

                        {/* Select Checkbox */}
                        <button
                          type="button"
                          onClick={() => toggleSelectPhoto(photo.photoKey)}
                          className={`absolute top-2 right-2 w-6 h-6 rounded-md flex items-center justify-center transition-all ${
                            isSelected
                              ? 'bg-orange-600 text-white'
                              : 'bg-white/80 text-gray-700 hover:bg-white'
                          }`}
                        >
                          <CheckCircle2 className="w-4 h-4" />
                        </button>

                        {/* Hover Overlay */}
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-between">
                          <button
                            type="button"
                            onClick={() => setActiveLightboxPhoto(photo)}
                            className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white text-xs flex items-center gap-1"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            <span>Preview</span>
                          </button>

                          <a
                            href={photo.photoUrl}
                            download={`BOW_Photo_${idx + 1}.jpg`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 rounded-lg bg-orange-600 hover:bg-orange-700 text-white text-xs flex items-center gap-1 shadow"
                          >
                            <Download className="w-3.5 h-3.5" />
                            <span>Save</span>
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="bg-white rounded-3xl p-12 text-center border border-gray-200">
                  <Camera className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                  <h3 className="text-lg font-bold text-gray-900 mb-1">No Faces Matched</h3>
                  <p className="text-xs text-gray-500 max-w-md mx-auto">
                    We couldn’t find an exact match in the selected album. Try snapping another selfie with clear front lighting or selecting "Search All Events".
                  </p>
                </div>
              )}

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 pt-6">
                  <button
                    disabled={currentPage === 1 || isSearching}
                    onClick={() => handleSearch(currentPage - 1)}
                    className="p-2.5 rounded-xl bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>

                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const page = i + 1;
                    return (
                      <button
                        key={page}
                        onClick={() => handleSearch(page)}
                        className={`w-10 h-10 rounded-xl font-bold text-sm transition-all ${
                          currentPage === page
                            ? 'bg-orange-600 text-white shadow-md'
                            : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}

                  <button
                    disabled={currentPage === totalPages || isSearching}
                    onClick={() => handleSearch(currentPage + 1)}
                    className="p-2.5 rounded-xl bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              )}
                </>
              )}

            </div>
          )}

        </div>
      </div>

      {/* Lightbox Modal */}
      {activeLightboxPhoto && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4">
          <button
            onClick={() => setActiveLightboxPhoto(null)}
            className="absolute top-5 right-5 text-white/70 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20"
          >
            <X className="w-6 h-6" />
          </button>

          <div className="max-w-4xl w-full max-h-[85vh] flex flex-col items-center">
            <img
              src={activeLightboxPhoto.photoUrl}
              alt="High Resolution Match"
              className="max-h-[75vh] w-auto object-contain rounded-2xl shadow-2xl"
            />
            <div className="mt-4 flex items-center justify-between w-full max-w-lg bg-white/10 backdrop-blur-sm px-5 py-2.5 rounded-2xl text-white text-xs">
              <span>Photographer: <strong>{activeLightboxPhoto.photographerName || 'BOW Media Team'}</strong></span>
              <a
                href={activeLightboxPhoto.photoUrl}
                download="BOW_Event_Photo.jpg"
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-1.5 rounded-xl bg-orange-600 hover:bg-orange-700 text-white font-bold flex items-center gap-1.5"
              >
                <Download className="w-3.5 h-3.5" />
                <span>Download High-Res</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FindMyPhotosPage;
